/**
 * The prompt catalog, from the page's side: what the Play and Audit buttons send.
 *
 * Always from the API — the organization's own wording where it has one. The desktop app keeps a
 * copy from the last time it reached the server, so a button still knows its prompt offline; the
 * browser does not need one.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { isDesktop } from './desktop'
import type { Locale } from './i18n'
import type { CatalogPrompt, PromptCatalog, WorkOrder } from './types'

const CACHE_KEY = 'devpulse.promptCatalog'

function cached(): PromptCatalog | undefined {
  if (!isDesktop()) return undefined
  try {
    const value = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as PromptCatalog | null
    return value?.prompts ? value : undefined
  } catch {
    return undefined
  }
}

function remember(catalog: PromptCatalog) {
  if (!isDesktop()) return
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(catalog))
  } catch {
    // Private storage: the next start fetches it again.
  }
}

export function usePromptCatalog() {
  return useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const catalog = await api.prompts.list()
      remember(catalog)
      return catalog
    },
    initialData: cached,
    // A cached copy is shown at once and refreshed behind it.
    initialDataUpdatedAt: 0,
    staleTime: 5 * 60_000,
  })
}

/** A prompt's title or description in the reader's language, English when it has none in theirs. */
export function localized(text: Record<string, string>, locale: Locale): string {
  return text[locale] ?? text['en-US'] ?? Object.values(text)[0] ?? ''
}

/** Who does a piece of work, as "Quem faz?" chose it (components/WhoDoesIt.tsx); empty: whoever of the person's asks. */
export interface WorkTarget {
  machine?: string | null
  agent?: string | null
  listener?: string | null
  builderDeveloperId?: string | null
}

/** Sends a catalog prompt to the agent in a repository, as the kind of work order it calls for — to whoever was chosen. */
export function runPrompt(prompt: CatalogPrompt, repositoryId: string | null, locale: Locale, target: WorkTarget = {}): Promise<WorkOrder> {
  return api.workOrders.create({
    kind: prompt.kind,
    instructions: prompt.body,
    title: localized(prompt.title, locale),
    repositoryId,
    maxIterations: prompt.kind === 'UntilDone' ? 5 : null,
    ...target,
  })
}
